import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { defineMessages } from 'react-intl.macro';

import { FormControlLabel, Grid, Radio, RadioGroup } from '@material-ui/core';
import { Theme, createStyles, withStyles, WithStyles } from '@material-ui/core/styles';
import { map } from 'lodash';
import { useFiltersContext } from '../../FiltersDialogContext';

const styles = ({ spacing: { unit } }: Theme) =>
  createStyles({
    radioGroup: {
      padding: `${unit * 5}px ${unit * 5}px 0 ${unit * 5}px`,
    },
    radio: {
      fontSize: 17,
    },
    padding: {
      padding: `${unit}px ${2 * unit}px`,
      paddingTop: 0,
    },
  });

const messages = defineMessages({
  hour: {
    id: 'core.FilterDateAggregation.hour',
    defaultMessage: 'Hour',
  },
  date: {
    id: 'core.FilterDateAggregation.date',
    defaultMessage: 'Date',
  },
  week: {
    id: 'core.FilterDateAggregation.week',
    defaultMessage: 'Week',
  },
  month: {
    id: 'core.FilterDateAggregation.month',
    defaultMessage: 'Month',
  },
  quarter: {
    id: 'core.FilterDateAggregation.quarter',
    defaultMessage: 'Quarter',
  },
  year: {
    id: 'core.FilterDateAggregation.year',
    defaultMessage: 'Year',
  },
});

export interface FilterDateAggregationProps extends WithStyles<typeof styles> {
  filterName: string;
  options?: ('hour' | 'date' | 'week' | 'month' | 'quarter' | 'year')[];
}

const FilterDateAggregation: React.FC<FilterDateAggregationProps> = ({
  classes,
  filterName,
  options = ['hour', 'date', 'week', 'month', 'quarter', 'year'],
}) => {
  const { formatMessage } = useIntl();
  const { filters, updater } = useFiltersContext();
  const dateUpdater = updater(filterName);

  // State
  const fallbackDateAggregation = filters[filterName];
  const [dateAggregation, setDateAggregation] = useState<string>(fallbackDateAggregation as string);

  // Force render when reset action occurs
  useEffect(() => {
    setDateAggregation(fallbackDateAggregation.toString());
  }, [fallbackDateAggregation]);

  // Handler for date aggregation change
  const handleRadioClick = (value: string) => {
    dateUpdater(value);
    setDateAggregation(value);
  };

  return (
    <>
      <Grid container spacing={8}>
        <Grid item>
          <RadioGroup
            className={classes.radioGroup}
            data-lh-id="date-aggregation-options"
            value={dateAggregation}
            onChange={(_, value) => handleRadioClick(value)}
            row
          >
            {map(options, (option) => (
              <FormControlLabel
                key={option}
                classes={{ label: classes.radio, root: classes.padding }}
                value={option}
                control={<Radio color="primary" data-lh-id={`date-aggregation-option-${option}`} />}
                label={formatMessage(messages[option])}
              />
            ))}
          </RadioGroup>
        </Grid>
      </Grid>
    </>
  );
};

export default withStyles(styles)(FilterDateAggregation);
